import React from 'react';
import {useRecoilState, useRecoilValue, useRecoilValueLoadable} from 'recoil';
import * as AppState from '../../AppState';
import numeral from 'numeral';
import zenscroll from 'zenscroll';
import { Property } from '../../interfaces/interfaces';
import CapcenterIcon from '../Map/MapMarkers/CapCenterIcon';
import AppHeader from './AppHeader';
import ListItem from './ListItem/ListItem';
import Colors from '../Shared/Colors';
import SidebarLoadingSkeleton from './SidebarLoadingSkeleton';
import { useSearchParams } from 'react-router-dom';

import './Sidebar.scss';

const List = () => {
	const gsProperties = useRecoilValueLoadable(AppState.gsProperties);
	const mapTilesLoading = useRecoilValue(AppState.gsMapTilesLoading);
	const gsClickedMarker = useRecoilValue(AppState.gsClickedMarker);
	const gsPageSize = useRecoilValue(AppState.gsPageSize);
	const [gsPageNumber, setGsPageNumber] = useRecoilState(AppState.gsPageNumber);
	const [searchParams] = useSearchParams();

	React.useEffect(() => {
		// Scroll property into view when map marker is clicked
		const scroller = zenscroll.createScroller(document.getElementById('propertiesList') as HTMLElement);
		const target = document.getElementById(gsClickedMarker) as HTMLElement;

		const highlightedProperties = document.getElementsByClassName('properties-list__list-item--active');
		highlightedProperties.length > 0 && highlightedProperties[0].classList.remove('properties-list__list-item--active');

		if (target) {
			scroller.center(target);
			target.classList.add('properties-list__list-item--active');
		}
	}, [gsClickedMarker]);

	function initializePageNumber() {
		const pageNumberQueryParameter = searchParams.get('page');
		if (pageNumberQueryParameter) {
			if (pageNumberQueryParameter !== gsPageNumber?.toString()) {
				setGsPageNumber(Number(pageNumberQueryParameter));
			}
		} else if (gsPageNumber) {
			setGsPageNumber(0);
		}
	}

	const PreviousPageButton = () => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const [searchParams, setSearchParams] = useSearchParams();
		const [gsPageNumber, setGsPageNumber] = useRecoilState(AppState.gsPageNumber);

		function previousPage() {
			if (gsPageNumber > 0) {
				const previousPageNumber = gsPageNumber - 1;

				setGsPageNumber(previousPageNumber);
				setSearchParams({['page']: previousPageNumber.toString()});
			}
		}

		return (
			<>
				<button type="button" className="btn btn-secondary mx-2 mt-2 col-3" aria-label="Show previous page of home listings" title="Show previous page of home listings" onClick={() => {
					previousPage();
				}}>Prev</button>
				<a href={`?page=${gsPageNumber-1}`} className="d-none">Previous page of home listings</a>
			</>
		);
	};

	const NextPageButton = () => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const [searchParams, setSearchParams] = useSearchParams();
		const [gsPageNumber, setGsPageNumber] = useRecoilState(AppState.gsPageNumber);

		function nextPage() {
			const nextPageNumber = gsPageNumber + 1;

			setGsPageNumber(nextPageNumber);
			setSearchParams({['page']: nextPageNumber.toString()});
		}

		return (
			<>
				<button type="button" className="btn btn-secondary mx-2 mt-2 col-3" aria-label="Show next page of home listings" title="Show next page of home listings" onClick={() => {
					nextPage();
				}}>Next</button>
				<a href={`?page=${gsPageNumber+1}`} className="d-none">Next page of home listings</a>
			</>
		);
	};

	const NoProperties = () => {
		return (
			<div className="d-flex flex-wrap justify-content-center w-100 mt-4">
				<div className="mb-2" style={{ width: '3rem', height: '3rem' }}>
					<CapcenterIcon fill={Colors.slate200} />
				</div>
				<h4 className="w-100 text-center fs-5 px-5" style={{ color: Colors.slate300 }}>
					No properties to show for this area
				</h4>
			</div>
		);
	};

	return (
		<>
			{initializePageNumber()}
			{(gsProperties.state === 'loading') ? <SidebarLoadingSkeleton /> :
				(
					<div
						id="propertiesList"
						className="properties-list__wrapper row g-0 justify-content-center align-content-start pb-5"
					>
						<div className="d-flex flex-lg-wrap flex-xl-nowrap w-100 justify-content-between justify-content-lg-center justify-content-xl-between align-items-center py-3 px-4">
							<AppHeader />
							<div className="small text-center flex-shrink-0 mt-lg-1 mt-xl-0">
								{mapTilesLoading ? ('Loading properties...') :
									(gsProperties.contents.total < gsPageSize) ? `Showing ${numeral(gsProperties.contents.total).format('0,0')} properties` :
										(`Showing ${(gsPageSize * gsPageNumber) + 1} to ${(gsPageSize * gsPageNumber) + gsProperties.contents.propertyArray.length} of ${numeral(gsProperties.contents.total).format('0,0')}`
										)}
							</div>
						</div>

						{gsProperties?.contents.propertyArray.length > 0 ?

							<>
								{
									gsProperties.contents.propertyArray.map((property: Property, i: number) => {
										return (
											<div className="properties-list__list-item-wrapper" key={i}>
												<ListItem property={property} address={property.UnparsedAddress || ''} />
											</div>
										);
									})
								}

								<div className="row justify-content-between mb-4">
									{
										(gsPageNumber > 0) ? <PreviousPageButton /> : <div className="col-3 mx-2 mt-2"></div>
									}
									{
										(((gsPageSize * gsPageNumber) + gsProperties.contents.propertyArray.length) < gsProperties.contents.total) ?
											<NextPageButton /> : <div className="col-3 mx-2 mt-2"></div>
									}
								</div>
							</>
							:
							<NoProperties />
						}
					</div>
				)}
		</>
	);
};

export default function Sidebar(props: { hidden: boolean }) {
	return (
		<aside className="home-search__sidebar" style={props?.hidden ? { display: 'none', zIndex: -1 } : { zIndex: 2 }}>
			<List />
		</aside >
	);
}
